import React, { useRef } from 'react';
import { bool, func, object, shape, string, oneOf } from 'prop-types';
import isEqual from 'lodash/isEqual';
import { compose } from 'redux';
import { withRouter } from 'react-router-dom';
import { intlShape, injectIntl } from '../../util/reactIntl';
import { connect } from 'react-redux';
import { types as sdkTypes } from '../../util/sdkLoader';
import routeConfiguration from '../../routeConfiguration';
import { createResourceLocatorString } from '../../util/routes';
import {
  LISTING_PAGE_PARAM_TYPE_DRAFT,
  LISTING_PAGE_PARAM_TYPE_NEW,
  LISTING_PAGE_PARAM_TYPES,
  LISTING_PAGE_PENDING_APPROVAL_VARIANT,
  createSlug,
} from '../../util/urlHelpers';
import { LISTING_STATE_DRAFT, LISTING_STATE_PENDING_APPROVAL, propTypes } from '../../util/types';
import { ensureOwnListing } from '../../util/data';
import * as log from '../../util/log';
import { getMarketplaceEntities } from '../../ducks/marketplaceData.duck';
import { manageDisableScrolling, isScrollingDisabled } from '../../ducks/UI.duck';
import {
  stripeAccountClearError,
  createStripeAccount,
  getStripeConnectAccountLink,
} from '../../ducks/stripeConnectAccount.duck';

import {
  LayoutSingleColumn,
  LayoutWrapperMain,
  LayoutWrapperTopbar,
  NamedRedirect,
  Page,
} from '../../components';
import { EditServiceListingForm } from '../../forms';
import { TopbarContainer } from '../../containers';

import {
  requestCreateListingDraft,
  requestUpdateListing,
  requestImageUpload,
  removeListingImage,
  loadData,
  savePayoutDetails,
  getRelatedPickerOptions,
} from './EditListingPage.duck';
import {
  editorListingState,
  imageIdsForSave,
  initialValuesFromListing,
  isImageUploading,
  saveTargetRoute,
  submitServiceListing,
} from './EditListingPage.helpers';
import EditListingPayoutModal from './EditListingPayoutModal';

import css from './EditListingPage.css';

const STRIPE_ONBOARDING_RETURN_URL_SUCCESS = 'success';
const STRIPE_ONBOARDING_RETURN_URL_FAILURE = 'failure';
const STRIPE_ONBOARDING_RETURN_URL_TYPES = [
  STRIPE_ONBOARDING_RETURN_URL_SUCCESS,
  STRIPE_ONBOARDING_RETURN_URL_FAILURE,
];

const { UUID } = sdkTypes;

// The listing is denormalised again on every render. Keep the same initial
// values until the saved listing's values change, so the form never
// re-initialises over unsaved edits. Uploads reach the form through `images`.
const useInitialValues = listing => {
  const initialValues = initialValuesFromListing(listing.id ? listing : null);
  const ref = useRef(initialValues);
  if (!isEqual(ref.current, initialValues)) {
    ref.current = initialValues;
  }
  return ref.current;
};

// N.B. All the presentational content needs to be extracted to their own components
export const EditListingPageComponent = props => {
  const {
    currentUser,
    createStripeAccountError,
    fetchStripeAccountError,
    getOwnListing,
    getRelatedOptions,
    getAccountLinkError,
    getAccountLinkInProgress,
    history,
    intl,
    onCreateListingDraft,
    onUpdateListing,
    onImageUpload,
    onRemoveListingImage,
    onManageDisableScrolling,
    onPayoutDetailsFormSubmit,
    onPayoutDetailsFormChange,
    onGetStripeConnectAccountLink,
    page,
    params,
    scrollingDisabled,
    stripeAccountFetched,
    stripeAccount,
    updateStripeAccountError,
  } = props;

  const { id, type, returnURLType } = params;
  const isNewURI = type === LISTING_PAGE_PARAM_TYPE_NEW;
  const isDraftURI = type === LISTING_PAGE_PARAM_TYPE_DRAFT;
  const isNewListingFlow = isNewURI || isDraftURI;

  const listingId = page.submittedListingId || (id ? new UUID(id) : null);
  const currentListing = ensureOwnListing(getOwnListing(listingId));
  const { state: currentListingState } = currentListing.attributes;
  const initialValues = useInitialValues(currentListing);

  const isPastDraft = currentListingState && currentListingState !== LISTING_STATE_DRAFT;
  const shouldRedirect = isNewListingFlow && listingId && isPastDraft;

  const hasStripeOnboardingDataIfNeeded = returnURLType ? !!(currentUser && currentUser.id) : true;
  const showForm = hasStripeOnboardingDataIfNeeded && (isNewURI || currentListing.id);

  const isCustomer =
    currentUser && currentUser.attributes.profile.publicData.account_role === 'customer';
  const canCreateListings = !isCustomer;

  if (!canCreateListings) {
    return <NamedRedirect name="LandingPage" />;
  }

  if (shouldRedirect) {
    const isPendingApproval =
      currentListing && currentListingState === LISTING_STATE_PENDING_APPROVAL;

    // If page has already listingId (after submit) and current listings exist
    // redirect to listing page
    const listingSlug = currentListing ? createSlug(currentListing.attributes.title) : null;

    const redirectProps = isPendingApproval
      ? {
          name: 'ListingPageVariant',
          params: {
            id: listingId.uuid,
            slug: listingSlug,
            variant: LISTING_PAGE_PENDING_APPROVAL_VARIANT,
          },
        }
      : {
          name: 'ListingPage',
          params: {
            id: listingId.uuid,
            slug: listingSlug,
          },
        };

    return <NamedRedirect {...redirectProps} />;
  } else if (showForm) {
    // Saved images, then uploads not yet attached to the listing, minus the
    // removed ones
    const currentListingImages =
      currentListing && currentListing.images ? currentListing.images : [];
    const imageOrder = page.imageOrder || [];
    const unattachedImages = imageOrder.map(i => page.images[i]);
    const allImages = currentListingImages.concat(unattachedImages);
    const removedImageIds = page.removedImageIds || [];
    const images = allImages.filter(img => {
      return !removedImageIds.includes(img.id);
    });

    const listingState = editorListingState(type, currentListing);
    const relatedOptions = getRelatedOptions(currentListing.id ? currentListing.id.uuid : null);
    // Without the loaded lists the save keeps the related picks as they are
    const relatedPools =
      relatedOptions.status === 'loaded'
        ? { works: relatedOptions.works, services: relatedOptions.services }
        : null;
    const saveInProgress = page.createListingDraftInProgress || page.updateInProgress;
    const saveError = page.createListingDraftError || page.updateListingError;

    const handleSubmit = values => {
      submitServiceListing({
        values,
        listing: currentListing,
        listingState,
        currentUser,
        imageIds: imageIdsForSave(values.images),
        relatedPools,
        onCreateListingDraft,
        onUpdateListing,
      })
        .then(listing => {
          const routes = routeConfiguration();
          if (isNewURI) {
            // Back from the listing page edits this draft instead of starting
            // another new listing
            const draftParams = {
              slug: createSlug(listing.attributes.title),
              id: listing.id.uuid,
              type: LISTING_PAGE_PARAM_TYPE_DRAFT,
              tab: params.tab,
            };
            history.replace(
              createResourceLocatorString('EditListingPage', routes, draftParams, {})
            );
          }
          const route = saveTargetRoute(listing, listingState);
          history.push(createResourceLocatorString(route.name, routes, route.params, {}));
        })
        .catch(e => {
          // The duck stores a save error and the form shows it; log it (and
          // anything that failed after the save) without bothering the user.
          log.error(e, 'service-listing-save-failed', {
            listingId: currentListing && currentListing.id ? currentListing.id.uuid : null,
          });
        });
    };

    const title = isNewListingFlow
      ? intl.formatMessage({ id: 'EditListingPage.titleCreateListing' })
      : intl.formatMessage({ id: 'EditListingPage.titleEditListing' });

    return (
      <Page title={title} scrollingDisabled={scrollingDisabled}>
        <LayoutSingleColumn>
          <LayoutWrapperTopbar>
            <TopbarContainer className={css.topbar} />
          </LayoutWrapperTopbar>
          <LayoutWrapperMain>
            <div className={css.main}>
              <EditServiceListingForm
                initialValues={initialValues}
                onSubmit={handleSubmit}
                images={images}
                onImageUpload={onImageUpload}
                onRemoveImage={onRemoveListingImage}
                listingState={listingState}
                saveInProgress={saveInProgress}
                imageUploadInProgress={images.some(isImageUploading)}
                imageUploadError={page.uploadImageError}
                saveError={saveError}
                onManageDisableScrolling={onManageDisableScrolling}
                relatedOptions={relatedOptions}
              />
            </div>
          </LayoutWrapperMain>
        </LayoutSingleColumn>
        <EditListingPayoutModal
          params={params}
          currentUser={currentUser}
          stripeOnboardingReturnURL={returnURLType}
          stripeAccount={stripeAccount}
          stripeAccountFetched={stripeAccountFetched}
          stripeAccountError={
            createStripeAccountError || updateStripeAccountError || fetchStripeAccountError
          }
          stripeAccountLinkError={getAccountLinkError}
          getAccountLinkInProgress={getAccountLinkInProgress}
          payoutDetailsSaveInProgress={page.payoutDetailsSaveInProgress}
          payoutDetailsSaved={page.payoutDetailsSaved}
          onPayoutDetailsFormChange={onPayoutDetailsFormChange}
          onPayoutDetailsSubmit={onPayoutDetailsFormSubmit}
          onGetStripeConnectAccountLink={onGetStripeConnectAccountLink}
          onManageDisableScrolling={onManageDisableScrolling}
        />
      </Page>
    );
  } else {
    // If user has come to this page through a direct linkto edit existing listing,
    // we need to load it first.
    const loadingPageMsg = {
      id: 'EditListingPage.loadingListingData',
    };
    return (
      <Page title={intl.formatMessage(loadingPageMsg)} scrollingDisabled={scrollingDisabled} />
    );
  }
};

EditListingPageComponent.defaultProps = {
  createStripeAccountError: null,
  fetchStripeAccountError: null,
  getAccountLinkError: null,
  getAccountLinkInProgress: null,
  stripeAccountFetched: null,
  currentUser: null,
  stripeAccount: null,
  currentUserHasOrders: null,
  listing: null,
  listingDraft: null,
  notificationCount: 0,
  sendVerificationEmailError: null,
};

EditListingPageComponent.propTypes = {
  createStripeAccountError: propTypes.error,
  fetchStripeAccountError: propTypes.error,
  getAccountLinkError: propTypes.error,
  getAccountLinkInProgress: bool,
  updateStripeAccountError: propTypes.error,
  currentUser: propTypes.currentUser,
  fetchInProgress: bool.isRequired,
  getOwnListing: func.isRequired,
  getRelatedOptions: func.isRequired,
  onGetStripeConnectAccountLink: func.isRequired,
  onCreateListingDraft: func.isRequired,
  onImageUpload: func.isRequired,
  onManageDisableScrolling: func.isRequired,
  onPayoutDetailsFormChange: func.isRequired,
  onPayoutDetailsFormSubmit: func.isRequired,
  onRemoveListingImage: func.isRequired,
  onUpdateListing: func.isRequired,
  page: object.isRequired,
  params: shape({
    id: string.isRequired,
    slug: string.isRequired,
    type: oneOf(LISTING_PAGE_PARAM_TYPES).isRequired,
    tab: string.isRequired,
    returnURLType: oneOf(STRIPE_ONBOARDING_RETURN_URL_TYPES),
  }).isRequired,
  stripeAccountFetched: bool,
  stripeAccount: object,
  scrollingDisabled: bool.isRequired,

  /* from withRouter */
  history: shape({
    push: func.isRequired,
    replace: func.isRequired,
  }).isRequired,

  /* from injectIntl */
  intl: intlShape.isRequired,
};

const mapStateToProps = state => {
  const page = state.EditListingPage;
  const {
    getAccountLinkInProgress,
    getAccountLinkError,
    createStripeAccountInProgress,
    createStripeAccountError,
    updateStripeAccountError,
    fetchStripeAccountError,
    stripeAccount,
    stripeAccountFetched,
  } = state.stripeConnectAccount;

  const { currentUser } = state.user;

  const fetchInProgress = createStripeAccountInProgress;

  const getOwnListing = id => {
    const listings = getMarketplaceEntities(state, [{ id, type: 'ownListing' }]);

    return listings.length === 1 ? listings[0] : null;
  };
  return {
    getAccountLinkInProgress,
    getAccountLinkError,
    createStripeAccountError,
    updateStripeAccountError,
    fetchStripeAccountError,
    stripeAccount,
    stripeAccountFetched,
    currentUser,
    fetchInProgress,
    getOwnListing,
    getRelatedOptions: listingId => getRelatedPickerOptions(state, listingId),
    page,
    scrollingDisabled: isScrollingDisabled(state),
  };
};

const mapDispatchToProps = dispatch => ({
  onUpdateListing: (tab, values, currentUser) =>
    dispatch(requestUpdateListing(tab, values, currentUser)),
  onCreateListingDraft: values => dispatch(requestCreateListingDraft(values)),
  onImageUpload: data => dispatch(requestImageUpload(data)),
  onManageDisableScrolling: (componentId, disableScrolling) =>
    dispatch(manageDisableScrolling(componentId, disableScrolling)),
  onPayoutDetailsFormChange: () => dispatch(stripeAccountClearError()),
  onPayoutDetailsSubmit: values => dispatch(createStripeAccount(values)),
  onPayoutDetailsFormSubmit: (values, isUpdateCall) =>
    dispatch(savePayoutDetails(values, isUpdateCall)),
  onGetStripeConnectAccountLink: params => dispatch(getStripeConnectAccountLink(params)),
  onRemoveListingImage: imageId => dispatch(removeListingImage(imageId)),
});

// Note: it is important that the withRouter HOC is **outside** the
// connect HOC, otherwise React Router won't rerender any Route
// components since connect implements a shouldComponentUpdate
// lifecycle hook.
//
// See: https://github.com/ReactTraining/react-router/issues/4671
const EditListingPage = compose(
  withRouter,
  connect(mapStateToProps, mapDispatchToProps)
)(injectIntl(EditListingPageComponent));

EditListingPage.loadData = loadData;

export default EditListingPage;
